import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from './AuthProvider';
import { fetchAndGetCSRFToken, getCSRFToken } from './utils/csrf';

function MFASetup() {
  const [qrData, setQrData] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [hasLoadedSetup, setHasLoadedSetup] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading, refreshUser } = useContext(AuthContext);
  const email = location.state?.email || '';

  useEffect(() => {
    if (authLoading || hasLoadedSetup) {
      return;
    }

    if (!user) {
      navigate('/login', { replace: true });
      return;
    }

    if (user.mfa_setup_completed) {
      if (user.mfa_verified) {
        navigate('/career-associates/', { replace: true });
      } else {
        navigate('/mfa-verify', { replace: true });
      }
      return;
    }

    async function loadSetup() {
      try {
        await fetchAndGetCSRFToken();
        const csrfToken = getCSRFToken();
        const response = await axios.post('/api/auth/mfa/setup', {}, {
          headers: { 'X-CSRFToken': csrfToken },
          withCredentials: true,
        });
        setQrData(response.data.qr_data_uri);
        setSecret(response.data.secret);
      } catch (err) {
        console.error('[MFASetup] Error loading MFA setup:', err);
        const errorMsg = err?.response?.data?.detail || err?.message || 'Unable to load MFA setup.';
        setError(errorMsg);
      } finally {
        setLoading(false);
      }
    }

    setHasLoadedSetup(true);
    loadSetup();
  }, [authLoading, hasLoadedSetup, user, navigate]);

  const handleVerify = async e => {
    e.preventDefault();
    setVerifying(true);
    setError('');
    try {
      await fetchAndGetCSRFToken();
      const csrfToken = getCSRFToken();
      await axios.post('/api/auth/mfa/setup/verify', { code }, {
        headers: { 'X-CSRFToken': csrfToken },
        withCredentials: true,
      });
      await refreshUser();
      navigate('/career-associates/');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Verification failed.');
    } finally {
      setVerifying(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-center">Loading MFA setup...</div>;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-xl w-full bg-white p-8 rounded-lg shadow-lg">
        <h2 className="text-2xl font-bold mb-4">Set up Authenticator</h2>
        <p className="mb-4 text-sm text-gray-600">
          Scan this QR code with Microsoft Authenticator, then enter the generated code below.
        </p>
        {error && <div className="mb-4 text-red-600">{error}</div>}
        {qrData ? (
          <div className="mb-6 text-center">
            <img
              src={qrData}
              alt="MFA QR code"
              className="mx-auto w-72 h-72 object-contain"
            />
            <p className="mt-4 text-xs text-gray-500 break-all">Secret: {secret}</p>
            {email && <p className="mt-1 text-xs text-gray-500">Email: {email}</p>}
            <p className="mt-2 text-xs text-gray-500">
              If scanning fails, manually enter the secret into Authenticator.
            </p>
          </div>
        ) : (
          <div className="mb-6 text-gray-700">Unable to render QR code.</div>
        )}
        <form onSubmit={handleVerify} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Authenticator Code</label>
            <input
              type="text"
              value={code}
              onChange={e => setCode(e.target.value)}
              required
              className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3"
            />
          </div>
          <button
            type="submit"
            disabled={verifying}
            className="w-full py-3 px-6 bg-indigo-600 text-white rounded-md shadow hover:bg-indigo-700 transition-all"
          >
            {verifying ? 'Verifying...' : 'Verify Code'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default MFASetup;
